import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";

export type TargetType = {
  id: number;
  type: string;
  name: string;
  requiresAuth: boolean;
};

type TargetTypeSelectorProps = {
  organisationId: string;
  value: number | null;
  onChange: (targetId: number, targetType: TargetType) => void;
  disabled?: boolean;
  /** When set, only show target types valid for this source type. */
  sourceConnectorType?: string;
};

/** Source types that should only have SqlServer as a target. */
const SQL_ONLY_SOURCES = new Set(["Xero"]);

export function TargetTypeSelector({
  organisationId,
  value,
  onChange,
  disabled,
  sourceConnectorType,
}: TargetTypeSelectorProps) {
  const { token } = useAuth();
  const [targets, setTargets] = useState<TargetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organisationId || !token) return;

    const fetchTargets = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await portalFetch<{ targets: TargetType[] }>(
          `/api/v1/gateway/business2cloud/api/v1/connectors/types/targets?organisationId=${organisationId}`,
          { token },
        );
        // Filter targets for source types that only support SqlServer
        const allTargets: TargetType[] = data.targets ?? [];
        const filtered =
          sourceConnectorType && SQL_ONLY_SOURCES.has(sourceConnectorType)
            ? allTargets.filter((t) => t.type === "SqlServer")
            : allTargets;
        setTargets(filtered);

        // Auto-select SqlServer as default if no value set
        if (!value && filtered.length > 0) {
          const sqlServerTarget = filtered.find(
            (t: TargetType) => t.type === "SqlServer",
          );
          if (sqlServerTarget) {
            onChange(sqlServerTarget.id, sqlServerTarget);
          } else {
            onChange(filtered[0].id, filtered[0]);
          }
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An error occurred";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, token]);

  const handleValueChange = (stringValue: string) => {
    const targetId = parseInt(stringValue, 10);
    const target = targets.find((t) => t.id === targetId);
    if (target) {
      onChange(targetId, target);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Label>Target Type</Label>
        <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading target types...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <Label>Target Type</Label>
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  if (targets.length === 0) {
    return (
      <div className="space-y-2">
        <Label>Target Type</Label>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          No target types available. Please contact support.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="target-type">Target Type</Label>
      <Select
        value={value?.toString() ?? ""}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger id="target-type">
          <SelectValue placeholder="Select target type" />
        </SelectTrigger>
        <SelectContent>
          {targets.map((target) => (
            <SelectItem key={target.id} value={target.id.toString()}>
              {target.name}
              {target.requiresAuth && (
                <span className="ml-2 text-xs text-slate-500">
                  (requires authorization)
                </span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500">
        Select where data from SQL Server will be sent
      </p>
    </div>
  );
}
